import { makeAutoObservable, runInAction } from 'mobx';
import agent from '../services/api/agent';
import { store } from './store';
import { ECreatorUserRole } from './user/userStore';
import SnackbarUtils from '../components/snackbarUtils/SnackbarUtilsConfigurator';

/** What the backend answers a caller who is not a superAdmin on the administrator endpoints. */
const HTTP_FORBIDDEN = 403;

export interface IRoles {
  id: string;
  name: string;
  description: string;
  users: IUser[];
}

export interface IUser {
  user_id: string;
  name: string;
  email: string;
  picture: string;
  created_at: Date;
  last_login: Date;
  logins_count: number;
  roles: string[];
}

export interface IOwnerIdModel {
  ownerID: number;
  nameIdentifier: string;
  mediaFolder: string;
  testOwnerID: string;
  ownerCourses: string;
  propertyValueMaps: string;
  mediaOwners: string;
  charedMediaOwners: string;
  testOwner: string;
  cheatingOwners: string;
}

export interface ITestOwner {
  ownerID: number;
  nameIdentifier: string;
  testOwnerID: number;
  user: ITestOwnerUser;
}

export interface ITestOwnerUser {
  userName: string;
  nickName: string;
  firstName: string;
  fullName: string;
  lastName: string;
  picture: string;
  userId: string;
}

//TODO: Endre sånn at testOwner bruker samme user klasse som i userStore...
export default class AdminStore {
  allUsers: IUser[] = [];
  roleUsers: IRoles[] = [];
  testOwner: ITestOwner | undefined = undefined; //The current user
  simulatedUserName = '';

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * True while a superAdmin works as another owner: the test owner swapped in is not the signed-in
   * owner. Read off the test owner itself, not the name shown for it, which may be empty.
   */
  get isImpersonating(): boolean {
    const testOwner = this.testOwner;
    return testOwner !== undefined && testOwner.user != null && testOwner.ownerID !== testOwner.testOwnerID;
  }

  /**
   * The owner a superAdmin works as, by id, or null while they work as themselves. What tells two test
   * owners apart: their display names may be the same, or empty.
   */
  get impersonatedOwnerId(): number | null {
    return this.isImpersonating && this.testOwner !== undefined ? this.testOwner.testOwnerID : null;
  }

  getAllUserRoles = async () => {
    const getAllUserRoles: any = await agent.User.GetAllUserRoles();

    this.roleUsers = getAllUserRoles.roles;
  };

  getAllUsers = async () => {
    const getAllUsers = await agent.User.GetAllUsers();
    this.allUsers = getAllUsers;
    return this.allUsers;
  };

  getTestOwner = async () => {
    const isTestOwner = localStorage.getItem('TestOwner') === 'true';
    if (isTestOwner) {
      let testOwner: ITestOwner;
      try {
        testOwner = await agent.Admin.GetTestOwner();
      } catch (error) {
        // The administrator endpoints are closed to all but superAdmins. A 403 here is a flag left in this
        // browser by an earlier superAdmin sign-in: there is no test owner, and the flag goes, quietly.
        if ((error as { response?: { status?: number } } | null)?.response?.status !== HTTP_FORBIDDEN) throw error;
        localStorage.removeItem('TestOwner');
        runInAction(() => {
          this.testOwner = undefined;
          this.simulatedUserName = '';
        });
        return;
      }
      this.testOwner = testOwner;

      if (store.userStore.role !== ECreatorUserRole.superAdmin && testOwner.nameIdentifier !== testOwner.user?.userId) {
        await this.resetTestOwner();
        return;
      }

      //Fiks denne til å ikke bruke fullName. Det er ikke alle som har den
      runInAction(() => {
        if (testOwner.user !== null && testOwner.ownerID !== testOwner.testOwnerID) {
          this.simulatedUserName = testOwner.user.fullName;
        }
      });
    }
  };

  setTestOwner = async (aUserID: string) => {
    console.log('aUserID', aUserID);
    const user = await agent.Admin.GetOwnerID(aUserID);
    console.log(user);
    if (user.length < 1) {
      SnackbarUtils.error('No ownerID found..');
      return;
    }
    await agent.Admin.SetTestOwner('myself', user[0].ownerID);

    localStorage.setItem('TestOwner', 'true');

    this.getTestOwner();
  };

  resetTestOwner = async () => {
    if (this.testOwner !== undefined) {
      await agent.Admin.SetTestOwner('myself', this.testOwner.ownerID);
      store.ownerStore.loadOwnerViewModel();
      this.getTestOwner();
      this.simulatedUserName = '';
      localStorage.setItem('TestOwner', 'false');
    }
  };
}
